<script lang="ts">
  import { onLoad } from "@otto-ec/global-resources/dom";
  /**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
  import type { ActionProps } from "./ActionSlotProps";
  import { useHintAndValidationMessage } from "./utils/useHintAndValidationMessage.svelte";

  let {
    Host,
    children = undefined,
    display = undefined,
    actionState = $bindable({
      active: false,
      checked: false,
      disabled: false,
      hover: false,
      invalid: false,
      placeholderShown: false,
      indeterminate: false,
      isInteractive: false,
    }),
  }: ActionProps = $props();

  const getActionElement = (): HTMLElement | undefined =>
    Array.from(Host.children).find((e): e is HTMLElement => e.slot === "");

  /*                                                                           */
  let actionElement = $state<HTMLElement | undefined>(getActionElement());

  $effect(() => {
    actionState.isInteractive = Boolean(
      actionElement && (actionElement.dataset.maskedRef || actionElement?.tabIndex >= 0),
    );
  });

  /*                                                                                                             */
  useHintAndValidationMessage(
    () => Host,
    () => actionElement,
  );

  /**
 *
 */
  const update = async (): Promise<void> => {
    if (!actionElement) return;

    const style = getComputedStyle(actionElement);
    const getFlag = (p: string): boolean => style.getPropertyValue(p) !== "0";

    const isInput = actionElement instanceof HTMLInputElement;

    Object.assign(actionState, {
      active: getFlag("--active"),
      checked: isInput ? (actionElement as HTMLInputElement).checked : getFlag("--checked"),
      disabled: isInput ? (actionElement as HTMLInputElement).disabled : getFlag("--disabled"),
      hover: getFlag("--hover"),
      invalid: getFlag("--invalid"),
      placeholderShown: getFlag("--placeholder-shown"),
      indeterminate: isInput
        ? (actionElement as HTMLInputElement).indeterminate
        : getFlag("--indeterminate"),
    });
  };

  /*                                                        */
  /*                                             */
  $effect(() => void ($onLoad && actionElement && update()));

  $effect(() => {
    if (!actionElement || !(actionElement instanceof HTMLInputElement)) return;
    const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "checked");
    if (!descriptor) return;

    Object.defineProperty(actionElement, "checked", {
      get: descriptor.get,
      set(value) {
        descriptor.set?.call(this, value);
        update();
      },
      configurable: true,
    });

    return () => {
      Object.defineProperty(actionElement, "checked", descriptor);
    };
  });
</script>

<svelte:element
  this={"slot"}
  class="action-slot"
  class:action-slot--is-interactive={actionState.isInteractive}
  style:display
  ontransitionstart={update}
  onslotchange={() => (actionElement = getActionElement())}>{@render children?.()}</svelte:element
>

<style lang="scss">
  /*                                                 */
  @use "sass:math";

  :global {
    .action-slot {
      /*                                                                                      */
      &::slotted(*) {
        all: unset;
        display: block; /*                                              */
        box-sizing: border-box;

        /*                                                                               */
        width: 100%;
        height: 100%;

        /*                                                            */
        --active: 0;
        --checked: 0;
        --disabled: 0;
        --hover: 0;
        --invalid: 0;
        --placeholder-shown: 0;
        --indeterminate: 0;
        box-shadow: 0 0 0 0
          rgba(
            /*                                                                   */
            calc(
                var(--active) + var(--checked) + var(--disabled) + var(--hover) + var(--invalid) +
                  var(--placeholder-shown) + var(--indeterminate)
              ),
            0,
            0,
            0
          ) !important;
        transition: box-shadow 1ms !important;
      }

      /*                                                                         */
      &::slotted(:empty) {
        position: absolute;
        inset: 0;
      }

      /*                                                                     */
      &::slotted(:disabled) {
        cursor: not-allowed;
      }

      /*                                                                                                                                                           */
      &::slotted(:not(:disabled)) {
        cursor: pointer;
      }

      /*                                               */
      &::slotted(:active) {
        --active: #{math.pow(2, 0)};
      }

      &::slotted(:checked),
      &::slotted([aria-checked="true"]) {
        --checked: #{math.pow(2, 1)};
      }

      &::slotted(:disabled),
      &::slotted([aria-disabled="true"]) {
        --disabled: #{math.pow(2, 2)};
      }

      &::slotted(:user-invalid),
      &::slotted([aria-invalid="true"]) {
        --invalid: #{math.pow(2, 3)};
      }

      &::slotted(:placeholder-shown) {
        --placeholder-shown: #{math.pow(2, 4)};
      }

      &::slotted(:indeterminate) {
        --indeterminate: #{math.pow(2, 6)};
      }
    }

    .action-slot--is-interactive {
      --oc-floating-focus-v1-full-bleed: "";

      &::slotted(*) {
        pointer-events: auto;
      }
    }

    .action-slot--is-interactive {
      --oc-floating-focus-v1-full-bleed: "";

      &::slotted(*) {
        pointer-events: auto;
      }
    }

    @media (hover: hover) {
      /*                                                                                  */
      :host(:hover) > .action-slot--is-interactive,
      :hover .action-slot--is-interactive {
        &::slotted(:not(:disabled)) {
          --hover: #{math.pow(2, 5)};
        }
      }
    }
  }
</style>
